/**
 * =============================================================================
 *  THAO TAC TRANG TREN pdf-lib
 * =============================================================================
 *
 *  Gop, tach, xoa, chen, xoay, sap xep lai. Day la bo tinh nang ma nguoi dung
 *  goi la "iLovePDF", va khac voi sua chu hay che thong tin, pdf-lib lam duoc
 *  TRON VEN trong trinh duyet. Khong co ly do gi de doi lo Rust/PDFium.
 *
 * =============================================================================
 *  MOT PHEP TINH DUY NHAT
 * =============================================================================
 *  MOI ham o day - ke ca sau ham cong khai theo dac ta - deu di qua DUNG MOT
 *  duong: dung mot PagePlan roi goi materializePlan(). Khong co ham nao tu sua
 *  byte theo cach rieng.
 *
 *  Day la bai hoc da tra gia o duong sua chu: khi ban xem truoc va duong xuat
 *  tinh hinh hoc bang HAI doan ma khac nhau, chung TROI khoi nhau, va ban xem
 *  truoc noi doi voi nguoi dung. Thanh ben hien so do trang nao thi save() vat
 *  chat hoa dung so do do, vi ca hai doc chung mot ket qua replayPagePlan().
 *
 * =============================================================================
 *  TINH TAT DINH - DIEU KIEN DE LUU HAI LAN RA HAI TEP GIONG HET NHAU
 * =============================================================================
 *  PDFDocument.create() mac dinh dong dau CreationDate/ModDate bang thoi diem
 *  hien tai. Neu de nguyen, hai lan luu cach nhau mot giay se cho ra hai luong
 *  byte KHAC NHAU, va ta khong con cach nao kiem chung "luu hai lan la giong
 *  nhau" - dung cai test bao ve chong loi ap lenh hai lan.
 *
 *  Vi vay: create({ updateMetadata: false }), load({ updateMetadata: false }),
 *  va sieu du lieu duoc SAO tu tai lieu nguon chu khong sinh moi.
 */
import { PDFDocument, degrees } from 'pdf-lib';
import type { PDFPage } from 'pdf-lib';

import {
  ORIGINAL_DOC_ID,
  buildInitialPlan,
  normalizeAngle,
  resolveBlankSizes,
  type BlankSize,
  type PagePlan,
  type PageSlot,
} from './plan';

export interface MaterializeOptions {
  /** Byte cua tai lieu goc (ma ORIGINAL_DOC_ID). */
  originalBytes: Uint8Array;
  /** Byte cua cac tai lieu ngoai da nhap, theo ma. Bat buoc du cho moi o. */
  sources?: ReadonlyMap<string, Uint8Array>;
}

/**
 * Dung tai lieu that tu mot so do trang.
 *
 * KHONG ghi vao `originalBytes`. pdf-lib doc buffer duoc truyen vao chu khong
 * sua no; ta van truyen ban sao de khong phu thuoc vao chi tiet trien khai do.
 */
export async function materializePlan(
  plan: PagePlan,
  options: MaterializeOptions,
): Promise<Uint8Array> {
  if (plan.length === 0) {
    throw new Error('So do trang rong: PDF phai co it nhat mot trang.');
  }

  const out = await PDFDocument.create({ updateMetadata: false });

  // --- Sao trang, gom theo tai lieu nguon ---------------------------------
  //
  // Mot lan load + mot lan copyPages cho moi tai lieu nguon. Trang trung lap
  // (cung tai lieu, cung chi so, xuat hien hai lan trong so do) VAN duoc yeu
  // cau hai lan: copyPages tra ve mot doi tuong trang RIENG cho moi muc, va
  // them cung mot doi tuong PDFPage vao tai lieu hai lan la hong.
  const requests = new Map<string, { slotIndex: number; sourcePageIndex: number }[]>();
  plan.forEach((slot, slotIndex) => {
    if (slot.sourcePageIndex === null) return;
    const list = requests.get(slot.docId);
    const entry = { slotIndex, sourcePageIndex: slot.sourcePageIndex };
    if (list) list.push(entry);
    else requests.set(slot.docId, [entry]);
  });

  const copied = new Array<PDFPage | null>(plan.length).fill(null);
  let originalDoc: PDFDocument | null = null;

  for (const [docId, list] of requests) {
    const bytes = docId === ORIGINAL_DOC_ID ? options.originalBytes : options.sources?.get(docId);
    if (!bytes) {
      throw new Error(`Thiếu byte của tài liệu nguồn "${docId}" khi dựng tệp.`);
    }
    const src = await PDFDocument.load(bytes.slice(), {
      ignoreEncryption: true,
      updateMetadata: false,
    });
    if (docId === ORIGINAL_DOC_ID) originalDoc = src;

    const count = src.getPageCount();
    for (const req of list) {
      if (req.sourcePageIndex < 0 || req.sourcePageIndex >= count) {
        throw new Error(
          `Tài liệu nguồn "${docId}" chỉ có ${count} trang, không có trang ${req.sourcePageIndex + 1}.`,
        );
      }
    }

    const pages = await out.copyPages(
      src,
      list.map((req) => req.sourcePageIndex),
    );
    list.forEach((req, k) => {
      copied[req.slotIndex] = pages[k] ?? null;
    });
  }

  // --- Kich thuoc cho trang trang 'auto' ----------------------------------
  const autoSizes = resolveAutoSizes(plan, copied);

  // --- Them trang theo DUNG thu tu so do ----------------------------------
  for (let i = 0; i < plan.length; i++) {
    const slot = plan[i] as PageSlot;
    const page = copied[i];
    if (page) {
      out.addPage(page);
    } else {
      const size = slot.blankSize === 'auto' || slot.blankSize === undefined
        ? (autoSizes[i] as BlankSize)
        : slot.blankSize;
      out.addPage([size.widthPt, size.heightPt]);
    }
  }

  // --- Xoay -----------------------------------------------------------------
  // Xoay la CONG THEM vao /Rotate san co cua trang, khong phai dat de len.
  // Mot trang scan da khai bao /Rotate 90 ma nguoi dung bam "xoay phai" thi
  // ket qua phai la 180, khong phai 90 (tuc la khong doi gi).
  const outPages = out.getPages();
  for (let i = 0; i < plan.length; i++) {
    const slot = plan[i] as PageSlot;
    if (slot.rotationDelta === 0) continue;
    const page = outPages[i];
    if (!page) continue;
    page.setRotation(degrees(normalizeAngle(page.getRotation().angle + slot.rotationDelta)));
  }

  if (originalDoc) copyDocumentMetadata(originalDoc, out);

  return out.save({ useObjectStreams: false });
}

/**
 * Kich thuoc cua trang trang 'auto'.
 *
 * Quy tac song trong pages/plan.ts (resolveBlankSizes) chu khong o day, vi
 * TRINH XEM cung phai tra loi dung cau hoi nay - no ve trang trang tren man
 * hinh truoc khi co bat ky tep nao. Hai ban sao cua quy tac se troi khoi nhau
 * va cho ra mot trang trang co kich thuoc khac giua ban xem truoc va tep xuat.
 * O day chi con phep doi tu PDFPage sang kich thuoc.
 */
function resolveAutoSizes(plan: PagePlan, copied: readonly (PDFPage | null)[]): BlankSize[] {
  return resolveBlankSizes(plan, (i) => {
    const page = copied[i];
    return page ? { widthPt: page.getWidth(), heightPt: page.getHeight() } : null;
  });
}

/** Giu lai sieu du lieu doc duoc. Ngay thang duoc SAO chu khong sinh moi. */
function copyDocumentMetadata(src: PDFDocument, dst: PDFDocument): void {
  try {
    const title = src.getTitle();
    if (title) dst.setTitle(title);
    const author = src.getAuthor();
    if (author) dst.setAuthor(author);
    const subject = src.getSubject();
    if (subject) dst.setSubject(subject);
    const keywords = src.getKeywords();
    if (keywords) dst.setKeywords([keywords]);
    const creator = src.getCreator();
    if (creator) dst.setCreator(creator);
    const created = src.getCreationDate();
    if (created) dst.setCreationDate(created);
    const modified = src.getModificationDate();
    if (modified) dst.setModificationDate(modified);
  } catch {
    // Sieu du lieu hong khong duoc lam hong ca thao tac trang. Bo qua co y.
  }
}

// ---------------------------------------------------------------------------
//  Sau thao tac cong khai. Tat ca deu la lop mong tren materializePlan().
// ---------------------------------------------------------------------------

async function planOf(bytes: Uint8Array): Promise<{ plan: PageSlot[]; pageCount: number }> {
  const doc = await PDFDocument.load(bytes.slice(), {
    ignoreEncryption: true,
    updateMetadata: false,
  });
  const pageCount = doc.getPageCount();
  return { plan: [...buildInitialPlan(pageCount)], pageCount };
}

function assertIndices(indices: readonly number[], pageCount: number): Set<number> {
  const set = new Set<number>();
  for (const i of indices) {
    if (!Number.isInteger(i) || i < 0 || i >= pageCount) {
      throw new Error(`Trang ${i + 1} không tồn tại (tài liệu có ${pageCount} trang).`);
    }
    set.add(i);
  }
  if (set.size === 0) throw new Error('Chưa chọn trang nào.');
  return set;
}

/** Xoa cac trang theo chi so (bat dau tu 0). Tra ve BYTE MOI; ban goc khong doi. */
export async function deletePages(
  bytes: Uint8Array,
  indices: readonly number[],
): Promise<Uint8Array> {
  const { plan, pageCount } = await planOf(bytes);
  const drop = assertIndices(indices, pageCount);
  if (drop.size >= pageCount) {
    throw new Error('Không xóa được toàn bộ trang: PDF phải có ít nhất một trang.');
  }
  const next = plan.filter((_, i) => !drop.has(i));
  return materializePlan(next, { originalBytes: bytes });
}

/**
 * Chuyen mot trang sang vi tri khac.
 * Ngu nghia: go trang o `from` ra, roi chen vao vi tri `to` cua danh sach DA
 * NGAN DI. Xem ghi chu ve lenh dao nguoc trong pages/plan.ts.
 */
export async function reorderPage(
  bytes: Uint8Array,
  from: number,
  to: number,
): Promise<Uint8Array> {
  const { plan, pageCount } = await planOf(bytes);
  assertIndices([from, to], pageCount);
  const next = [...plan];
  const [moved] = next.splice(from, 1);
  if (!moved) throw new Error(`Trang ${from + 1} không tồn tại.`);
  next.splice(to, 0, moved);
  return materializePlan(next, { originalBytes: bytes });
}

/** Xoay CONG THEM `delta` do cho cac trang duoc chon. */
export async function rotatePages(
  bytes: Uint8Array,
  indices: readonly number[],
  delta: number,
): Promise<Uint8Array> {
  const { plan, pageCount } = await planOf(bytes);
  const targets = assertIndices(indices, pageCount);
  const next = plan.map((slot, i) =>
    targets.has(i) ? { ...slot, rotationDelta: normalizeAngle(slot.rotationDelta + delta) } : slot,
  );
  return materializePlan(next, { originalBytes: bytes });
}

/** Chen mot trang trang tai `atIndex`. `atIndex === pageCount` la chen vao cuoi. */
export async function insertBlankPage(
  bytes: Uint8Array,
  atIndex: number,
  size?: BlankSize,
): Promise<Uint8Array> {
  const { plan, pageCount } = await planOf(bytes);
  if (!Number.isInteger(atIndex) || atIndex < 0 || atIndex > pageCount) {
    throw new Error(`Không chèn được ở vị trí ${atIndex + 1} (tài liệu có ${pageCount} trang).`);
  }
  const next = [...plan];
  next.splice(atIndex, 0, {
    docId: ORIGINAL_DOC_ID,
    sourcePageIndex: null,
    rotationDelta: 0,
    blankSize: size ?? 'auto',
  });
  return materializePlan(next, { originalBytes: bytes });
}

/** Trich cac trang duoc chon ra mot PDF MOI, giu dung thu tu tang dan. */
export async function extractPages(
  bytes: Uint8Array,
  indices: readonly number[],
): Promise<Uint8Array> {
  const { plan, pageCount } = await planOf(bytes);
  const keep = assertIndices(indices, pageCount);
  const next = plan.filter((_, i) => keep.has(i));
  return materializePlan(next, { originalBytes: bytes });
}

/** Tach thanh tung trang roi. Ket qua theo dung thu tu trang. */
export async function splitIntoSinglePages(bytes: Uint8Array): Promise<Uint8Array[]> {
  const { pageCount } = await planOf(bytes);
  const out: Uint8Array[] = [];
  for (let i = 0; i < pageCount; i++) {
    out.push(await extractPages(bytes, [i]));
  }
  return out;
}

export interface MergeInput {
  /** Ten hien cho nguoi dung khi bao loi. */
  name: string;
  bytes: Uint8Array;
}

export interface MergeFailure {
  name: string;
  reason: string;
}

export interface MergeResult {
  bytes: Uint8Array;
  /** Cac tep da gop, theo thu tu, kem so trang lay tu moi tep. */
  merged: { name: string; pageCount: number }[];
  /** Tep khong doc duoc. Bao TUNG TEP, khong huy ca lan gop. */
  failures: MergeFailure[];
}

/**
 * Gop nhieu PDF thanh mot.
 *
 * Day la thao tac DUY NHAT mang byte LA vao tai lieu dang lam viec, nen no la
 * cho duy nhat trong nhom nay can mot cua kiem tra rieng: MOI tep phai phan
 * tich duoc TRUOC khi ta dung tai lieu ket qua. Mot tep hong khong duoc lam
 * hong ca lan gop - no bi loai ra va bao ten len cho nguoi dung.
 */
export async function mergeDocuments(inputs: readonly MergeInput[]): Promise<MergeResult> {
  if (inputs.length === 0) throw new Error('Chưa chọn tệp nào để ghép.');

  const failures: MergeFailure[] = [];
  const good: { name: string; bytes: Uint8Array; pageCount: number }[] = [];

  // Vong doc: chi PHAN TICH, chua dung gi ca.
  for (const input of inputs) {
    try {
      const doc = await PDFDocument.load(input.bytes.slice(), {
        ignoreEncryption: true,
        updateMetadata: false,
      });
      const pageCount = doc.getPageCount();
      if (pageCount === 0) {
        failures.push({ name: input.name, reason: 'Tệp không có trang nào.' });
        continue;
      }
      good.push({ name: input.name, bytes: input.bytes, pageCount });
    } catch (err) {
      failures.push({
        name: input.name,
        reason: `Không đọc được: ${String(err).slice(0, 120)}`,
      });
    }
  }

  if (good.length === 0) {
    throw new Error('Không tệp nào trong danh sách đọc được, chưa ghép được gì.');
  }

  // Vong dung: tep dau tien lam tai lieu goc, cac tep sau la nguon ngoai.
  const first = good[0] as { name: string; bytes: Uint8Array; pageCount: number };
  const sources = new Map<string, Uint8Array>();
  const plan: PageSlot[] = [];

  for (let i = 0; i < first.pageCount; i++) {
    plan.push({ docId: ORIGINAL_DOC_ID, sourcePageIndex: i, rotationDelta: 0 });
  }
  for (let d = 1; d < good.length; d++) {
    const entry = good[d] as { name: string; bytes: Uint8Array; pageCount: number };
    const docId = `merge-${d}`;
    sources.set(docId, entry.bytes);
    for (let i = 0; i < entry.pageCount; i++) {
      plan.push({ docId, sourcePageIndex: i, rotationDelta: 0 });
    }
  }

  const bytes = await materializePlan(plan, { originalBytes: first.bytes, sources });
  return {
    bytes,
    merged: good.map((g) => ({ name: g.name, pageCount: g.pageCount })),
    failures,
  };
}
